import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Typography,
  Row,
  Col,
  Statistic,
  message,
  Tooltip,
  Divider,
  Tag,
  Space,
} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  BarChartOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { customGroupsAPI } from '../services/api';
import Loader from '../components/Loader';
import { generateCustomGroupReportPdf } from '../utils/customGroupReportPdf';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

interface AnalyticsData {
  customGroup: {
    _id: string;
    name: string;
    description: string;
  };
  overview: {
    totalGroups: number;
    totalMembers: number;
    uniqueMembers: number;
    avgGroupSize: number;
  };
  groupsByAccount: Array<{
    accountId: string;
    accountName: string;
    phoneNumber: string;
    groupsCount: number;
    membersCount: number;
    groups: Array<{
      _id: string;
      name: string;
      participantsCount: number;
    }>;
  }>;
  groupsBySize: {
    '0-10': number;
    '11-25': number;
    '26-50': number;
    '51-100': number;
    '101-200': number;
    '201-500': number;
    '500+': number;
  };
  topGroups: Array<{
    _id: string;
    name: string;
    participantsCount: number;
    accountName: string;
  }>;
  allGroups: Array<{
    _id: string;
    name: string;
    description: string;
    participantsCount: number;
    accountId: any;
    participants: Array<{
      id: string;
      name: string;
      phoneNumber: string;
      isAdmin: boolean;
      isSuperAdmin: boolean;
    }>;
  }>;
}

const CustomGroupAnalytics: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('all');
  const [memberSearchText, setMemberSearchText] = useState('');
  const [groupFilterSearch, setGroupFilterSearch] = useState('');

  useEffect(() => {
    if (id) {
      fetchAnalytics();
    }
  }, [id]);

  const fetchAnalytics = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await customGroupsAPI.getAnalytics(id);
      setAnalyticsData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch analytics');
      message.error(err.response?.data?.error || 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const filteredGroupsForSelect = useMemo(() => {
    if (!analyticsData) return [];
    if (!groupFilterSearch) return analyticsData.allGroups;
    
    const searchLower = groupFilterSearch.toLowerCase();
    return analyticsData.allGroups.filter((group) =>
      group.name.toLowerCase().includes(searchLower) ||
      group.accountId?.accountName?.toLowerCase().includes(searchLower)
    );
  }, [analyticsData, groupFilterSearch]);

  const filteredMembers = useMemo(() => {
    if (!analyticsData) return [];
    
    let members: Array<{
      id: string;
      name: string;
      phoneNumber: string;
      isAdmin: boolean;
      isSuperAdmin: boolean;
      groupId: string;
      groupName: string;
      accountName: string;
    }> = [];

    // Collect all members from all groups or selected group
    analyticsData.allGroups.forEach((group) => {
      if (selectedGroupId === 'all' || group._id === selectedGroupId) {
        group.participants.forEach((participant) => {
          members.push({
            ...participant,
            groupId: group._id,
            groupName: group.name,
            accountName: group.accountId?.accountName || 'Unknown',
          });
        });
      }
    });

    // Filter by search text
    if (memberSearchText) {
      const searchLower = memberSearchText.toLowerCase();
      members = members.filter(
        (member) =>
          member.name?.toLowerCase().includes(searchLower) ||
          member.phoneNumber?.toLowerCase().includes(searchLower) ||
          member.groupName?.toLowerCase().includes(searchLower) ||
          member.accountName?.toLowerCase().includes(searchLower)
      );
    }

    return members;
  }, [analyticsData, selectedGroupId, memberSearchText]);

  const downloadReport = () => {
    if (!analyticsData) return;
    try {
      generateCustomGroupReportPdf(analyticsData);
      message.success('Report downloaded successfully');
    } catch (err) {
      console.error('Error generating PDF:', err);
      message.error('Error generating PDF report. Please try again.');
    }
  };

  const memberColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any) => (
        <div>
          <Text strong>{text || 'Unknown'}</Text>
          {record.isSuperAdmin && (
            <Tag color="red" style={{ marginLeft: 8 }}>Super Admin</Tag>
          )}
          {record.isAdmin && !record.isSuperAdmin && (
            <Tag color="blue" style={{ marginLeft: 8 }}>Admin</Tag>
          )}
        </div>
      ),
      sorter: (a: any, b: any) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (text: string, record: any) => text || record.id || 'N/A',
      sorter: (a: any, b: any) => (a.phoneNumber || a.id || '').localeCompare(b.phoneNumber || b.id || ''),
    },
    {
      title: 'Group',
      dataIndex: 'groupName',
      key: 'groupName',
      render: (text: string, record: any) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.accountName}
          </Text>
        </div>
      ),
      sorter: (a: any, b: any) => a.groupName.localeCompare(b.groupName),
    },
    {
      title: 'Role',
      key: 'role',
      render: (_: any, record: any) => {
        if (record.isSuperAdmin) {
          return <Tag color="red">Super Admin</Tag>;
        }
        if (record.isAdmin) {
          return <Tag color="blue">Admin</Tag>;
        }
        return <Tag>Member</Tag>;
      },
    },
  ];

  if (loading) {
    return <Loader fullScreen text="Loading analytics..." />;
  }

  if (error || !analyticsData) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/custom-groups')}
            style={{ marginBottom: 16 }}
          >
            Back to Custom Groups
          </Button>
          <Card>
            <div className="text-center py-12">
              <Text type="danger">{error || 'Analytics data not found'}</Text>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/custom-groups')}
            >
              Back
            </Button>
            <div>
              <Title level={2} className="mb-0">
                {analyticsData.customGroup.name} - Analytics
              </Title>
              {analyticsData.customGroup.description && (
                <Text type="secondary">{analyticsData.customGroup.description}</Text>
              )}
            </div>
          </div>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            size="large"
            onClick={downloadReport}
          >
            Download PDF Report
          </Button>
        </div>

        {/* Overview Stats */}
        <Row gutter={16} className="mb-6">
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Groups"
                value={analyticsData.overview.totalGroups}
                valueStyle={{ color: '#0e4842' }}
                prefix={<BarChartOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Members"
                value={analyticsData.overview.totalMembers}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Unique Members"
                value={analyticsData.overview.uniqueMembers}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Avg Group Size"
                value={analyticsData.overview.avgGroupSize}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Groups by Account */}
        <Card className="mb-6">
          <Title level={4}>Groups by Account</Title>
          <Table
            dataSource={analyticsData.groupsByAccount}
            rowKey="accountId"
            pagination={false}
            size="small"
            columns={[
              {
                title: 'Account',
                key: 'account',
                render: (_: any, record: any) => (
                  <div>
                    <Text strong>{record.accountName}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {record.phoneNumber}
                    </Text>
                  </div>
                ),
              },
              {
                title: 'Groups',
                dataIndex: 'groupsCount',
                key: 'groupsCount',
              },
              {
                title: 'Members',
                dataIndex: 'membersCount',
                key: 'membersCount',
                render: (count: number) => count.toLocaleString(),
              },
            ]}
          />
        </Card>

        {/* Groups by Size */}
        <Card className="mb-6">
          <Title level={4}>Groups Distribution by Size</Title>
          <div className="space-y-3">
            {Object.entries(analyticsData.groupsBySize).map(([size, count]) => (
              <div key={size} className="flex items-center gap-4">
                <Text className="w-24 text-sm font-medium">{size}</Text>
                <div className="flex-1 h-6 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#0e4842] to-[#14a085] rounded-full transition-all"
                    style={{
                      width: `${(count / analyticsData.overview.totalGroups) * 100}%`,
                    }}
                  />
                </div>
                <Text className="w-12 text-right font-medium">{count}</Text>
              </div>
            ))}
          </div>
        </Card>

        {/* Top Groups */}
        <Card className="mb-6">
          <Title level={4}>Top Groups by Size</Title>
          <Table
            dataSource={analyticsData.topGroups}
            rowKey="_id"
            pagination={false}
            size="small"
            columns={[
              {
                title: '#',
                key: 'index',
                render: (_: any, __: any, index: number) => index + 1,
                width: 50,
              },
              {
                title: 'Group Name',
                dataIndex: 'name',
                key: 'name',
              },
              {
                title: 'Account',
                dataIndex: 'accountName',
                key: 'accountName',
              },
              {
                title: 'Members',
                dataIndex: 'participantsCount',
                key: 'participantsCount',
                render: (count: number) => count.toLocaleString(),
              },
            ]}
          />
        </Card>

        {/* Member List with Group Filter */}
        <Card>
          <div className="mb-4">
            <Title level={4}>Member List</Title>
            <div className="flex gap-4 mt-4">
              <Select
                placeholder="Filter by Group"
                style={{ width: 300 }}
                size="large"
                value={selectedGroupId}
                onChange={setSelectedGroupId}
                showSearch
                filterOption={false}
                onSearch={setGroupFilterSearch}
                optionLabelProp="label"
                dropdownRender={(menu) => (
                  <>
                    <div style={{ padding: '8px' }}>
                      <Input
                        placeholder="Search groups by name or account..."
                        value={groupFilterSearch}
                        onChange={(e) => setGroupFilterSearch(e.target.value)}
                        allowClear
                        prefix={<SearchOutlined />}
                      />
                    </div>
                    <Divider style={{ margin: '8px 0' }} />
                    {menu}
                  </>
                )}
                notFoundContent={
                  analyticsData.allGroups.length === 0 ? (
                    <div style={{ padding: '12px', textAlign: 'center' }}>
                      <Text type="secondary">No groups available</Text>
                    </div>
                  ) : groupFilterSearch && filteredGroupsForSelect.length === 0 ? (
                    <div style={{ padding: '12px', textAlign: 'center' }}>
                      <Text type="secondary">
                        No groups found matching "{groupFilterSearch}"
                      </Text>
                    </div>
                  ) : null
                }
              >
                <Option value="all" label="All Groups">
                  All Groups
                </Option>
                {filteredGroupsForSelect.map((group) => (
                  <Option key={group._id} value={group._id} label={group.name}>
                    <div>
                      <Text strong>{group.name}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {group.accountId?.accountName || 'Unknown'} • {group.participantsCount} members
                      </Text>
                    </div>
                  </Option>
                ))}
              </Select>
              <Search
                placeholder="Search members by name, phone, or group..."
                allowClear
                enterButton={<SearchOutlined />}
                size="large"
                style={{ flex: 1, maxWidth: 400 }}
                value={memberSearchText}
                onChange={(e) => setMemberSearchText(e.target.value)}
              />
            </div>
            <div className="mt-2">
              <Text type="secondary">
                Showing {filteredMembers.length} member{filteredMembers.length !== 1 ? 's' : ''}
                {selectedGroupId !== 'all' && ` in selected group`}
              </Text>
            </div>
          </div>
          <Table
            columns={memberColumns}
            dataSource={filteredMembers}
            rowKey={(record) => `${record.groupId}-${record.id || record.phoneNumber}`}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} members`,
            }}
            scroll={{ x: 800 }}
          />
        </Card>
      </div>
    </div>
  );
};

export default CustomGroupAnalytics;
